import { Component, OnInit } from '@angular/core';
import { MenuService } from '@app/shared/services/menu.service';
import { TitleService } from '@app/shared/services/title.service';
import { SocietiesService } from '@app/shared/services/societies.service';
import { ExcelService } from '@app/shared/services/excel.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/internal/operators';
import { AlertService } from '@app/shared/services/alert.service';

@Component({
    selector: 'app-society-list',
    templateUrl: './society-list.component.html',
    styleUrls: ['./society-list.component.scss']
})
export class SocietyListComponent implements OnInit {

    public societySearch;
    public societies: Array<any> = [];
    public maxPages: number = 1;
    public search: string = '';
    public searchChanged: Subject<string> = new Subject<string>();
    public currentPage: number = 1;
    public loadingStatus: boolean = true;
    public noResult: boolean = false;
    public excelLoading: boolean = false;
    private alertService: AlertService;

    constructor(
        private titleService: TitleService,
        private menu: MenuService,
        private societyService: SocietiesService,
        private excelService: ExcelService
    ) {
        this.searchChanged
            .pipe(debounceTime(1000), distinctUntilChanged())
            .subscribe(model => {
                this.search = model;
                this.currentPage = 1;
                sessionStorage.setItem('SocietyFilterSearch', this.search);
                this.updateSocieties();
            });
    }

    retrieveFilters() {
        this.search = ('SocietyFilterSearch' in sessionStorage) ? sessionStorage.getItem('SocietyFilterSearch') : this.search;
    }

    ngOnInit() {
        this.titleService.setTitle(`Liste des sociétés / VOP`);
        this.menu.setMenuState('adherent', 'society', '');
        this.retrieveFilters();
        if ('SocietyList' in sessionStorage) {
            this.societies = JSON.parse(sessionStorage.getItem('SocietyList'));
            this.maxPages = parseInt(sessionStorage.getItem('SocietyListMaxPages'), 10);
            this.currentPage = parseInt(sessionStorage.getItem('SocietyListCurrentPage'), 10);
            this.loadingStatus = false;
        } else {
            this.updateSocieties();
        }
    }

    onSearchChange(search): void {
        this.searchChanged.next(search);
    }

    setUsers(res) {
        this.societies = res;
        sessionStorage.setItem('SocietyList', JSON.stringify(this.societies));
        sessionStorage.setItem('SocietyListMaxPages', this.maxPages.toString());
        sessionStorage.setItem('SocietyListCurrentPage', this.currentPage.toString());
    }

    updateSocieties() {
        this.loadingStatus = true;
        this.currentPage = 1;
        this.societyService.updateSocietyCount(this.search).then(() => {
            this.maxPages = this.societyService.maxPages;
        });
        this.societyService.updateSocieties(this.currentPage, this.search).then((res) => {
            this.loadingStatus = false;
            if (this.societyService.societies.length === 0) {
                this.noResult = true;
            } else {
                this.societies = res;
                this.noResult = false;
            }
        });
    }

    nextPage(): any {
        this.loadingStatus = true;
        this.societyService.updateSocietyCount(this.search).then(() => {
            this.maxPages = this.societyService.maxPages;
            if (this.currentPage < this.societyService.maxPages) {
                this.currentPage += 1;
                this.societyService.updateSocieties(this.currentPage, this.search).then((res) => {
                    this.loadingStatus = false;
                    if (this.societyService.societies.length === 0) {
                        this.noResult = true;
                    } else {
                        this.societies = res;
                        this.noResult = false;
                    }
                });
            }
        });
    }

    goPage(page): any {
        this.loadingStatus = true;
        this.societyService.updateSocietyCount(this.search).then(() => {
            this.maxPages = this.societyService.maxPages;
            this.currentPage = page;
            this.societyService.updateSocieties(this.currentPage, this.search).then((res) => {
                this.loadingStatus = false;
                if (this.societyService.societies.length === 0) {
                    this.noResult = true;
                } else {
                    this.noResult = false;
                    this.societies = res;
                }
            });
        });
    }

    prevPage(): any {
        this.loadingStatus = true;
        this.societyService.updateSocietyCount(this.search).then(() => {
            this.maxPages = this.societyService.maxPages;
            if (this.currentPage > 1) {
                this.currentPage -= 1;
                this.societyService.updateSocieties(this.currentPage, this.search).then((res) => {
                    this.loadingStatus = false;
                    if (this.societyService.societies.length === 0) {
                        this.noResult = true;
                    } else {
                        this.societies = res;
                        this.noResult = false;
                    }
                });
            }
        });
    }

    exportDataAsXLSX(): any {
        let datas = [];
        let cols = this.excelService.initCols(["Nom", "Adresse", "Téléphone", "Code adhérent",
        "Nom de l'administrateur", "E-mail administrateur", "Téléphone administrateur",
        "Nom du manager", "E-mail manager", "Téléphone manager"]);

        this.excelLoading = true;
        this.societyService.updateSocietyCount().then(async () => {
            this.maxPages = Math.max(this.societyService.maxPages, 1);
            for (let i = 1; i <= this.maxPages; i++) {
                let results = await this.societyService.updateSocieties(i, '');

                if(results.length == 0)
                {
                    this.excelLoading = false;
                    return this.alertService.alert("Aucune donnée à exporter");
                }

                results.forEach((result) => {
                    let data = {
                        "Nom": result.Name,
                        "Adresse": result.Address,
                        "Téléphone": result.Mobile,
                        "Code adhérent": result.Code,
                        "Nom de l'administrateur": (result.AdminFirstName !== null && result.AdminLastName !== null) ? result.AdminFirstName + ' ' + result.AdminLastName : null,
                        "E-mail administrateur": result.AdminEMailAddress,
                        "Téléphone administrateur": result.AdminMobile,
                        "Nom du manager": (result.ManagerFirstName !== null && result.ManagerLastName !== null) ? result.ManagerFirstName + ' ' + result.ManagerLastName : null,
                        "E-mail manager": result.ManagerEMailAddress,
                        "Téléphone manager": result.ManagerMobile
                    };

                    Object.values(data).forEach((value, index) => {
                        if (typeof value === 'string') {
                            cols[index].wch = Math.max(value.length, cols[index].wch);
                        }
                    });
                    datas.push(data);
                });
            }
            cols = this.excelService.extendCols(cols);
            this.excelService.exportExcel(datas, "vop-societies", cols);
            this.excelLoading = false;
        });
    }

}
